import { SnippetTextarea } from '@/components/common/SnippetTextarea.js';
import { TicketNoteComposer } from '@/components/tickets/TicketNoteComposer.js';

export type ComposeMode = 'note' | 'private' | 'sms' | 'email';

const MODE_LABELS: Record<ComposeMode, string> = {
  note: 'Public Note',
  private: 'Private Note',
  sms: 'SMS',
  email: 'Email',
};

export function TicketComposeFields({
  composeMode,
  onComposeModeChange,
  composeBody,
  onComposeBodyChange,
  smsPhone,
  onSmsPhoneChange,
  emailTo,
  onEmailToChange,
  emailSubject,
  onEmailSubjectChange,
  onSubmit,
  submitting,
}: {
  composeMode: ComposeMode;
  onComposeModeChange: (mode: ComposeMode) => void;
  composeBody: string;
  onComposeBodyChange: (v: string) => void;
  smsPhone: string;
  onSmsPhoneChange: (v: string) => void;
  emailTo: string;
  onEmailToChange: (v: string) => void;
  emailSubject: string;
  onEmailSubjectChange: (v: string) => void;
  onSubmit: () => void;
  submitting: boolean;
}) {
  return (
    <div>
      <div className="mb-3 flex flex-wrap gap-2">
        {(['note', 'private', 'sms', 'email'] as ComposeMode[]).map((m) => (
          <button
            key={m}
            type="button"
            className={`rounded-lg px-3 py-1.5 text-xs font-semibold ${composeMode === m ? 'bg-primary text-white' : 'bg-slate-100 text-slate-700'}`}
            onClick={() => onComposeModeChange(m)}
          >
            {m === 'note' ? '📝 Note' : m === 'private' ? '🔒 Private' : m === 'sms' ? '📱 SMS' : '📧 Email'}
          </button>
        ))}
      </div>
      <p className="mb-2 text-xs font-semibold uppercase text-slate-500">Mode: {MODE_LABELS[composeMode]}</p>
      {composeMode === 'sms' ? (
        <input
          className="mb-2 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
          placeholder="Phone number"
          value={smsPhone}
          onChange={(e) => onSmsPhoneChange(e.target.value)}
        />
      ) : null}
      {composeMode === 'email' ? (
        <>
          <input
            className="mb-2 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
            placeholder="To"
            value={emailTo}
            onChange={(e) => onEmailToChange(e.target.value)}
          />
          <input
            className="mb-2 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
            placeholder="Subject"
            value={emailSubject}
            onChange={(e) => onEmailSubjectChange(e.target.value)}
          />
        </>
      ) : null}
      {composeMode === 'sms' || composeMode === 'email' ? (
        <>
          <SnippetTextarea
            value={composeBody}
            onChange={onComposeBodyChange}
            placeholder={
              composeMode === 'sms'
                ? 'SMS message (160 chars recommended) — type / to insert a snippet'
                : 'Write your message… type / to insert a snippet'
            }
            maxLength={composeMode === 'sms' ? 1600 : undefined}
          />
          {composeMode === 'sms' ? (
            <p className="mt-1 text-xs text-slate-500">{composeBody.length} / 160 characters</p>
          ) : null}
          <div className="mt-3 flex justify-end">
            <button
              type="button"
              disabled={submitting || !composeBody.trim()}
              className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
              onClick={onSubmit}
            >
              {composeMode === 'sms' ? 'Send SMS →' : 'Send Email →'}
            </button>
          </div>
        </>
      ) : (
        <TicketNoteComposer
          value={composeBody}
          onChange={onComposeBodyChange}
          onSubmit={onSubmit}
          submitting={submitting}
          submitLabel="Save Note →"
          placeholder="Write your message… type / to insert a snippet"
        />
      )}
    </div>
  );
}
